import { IMedia } from '@rendora/common/entities/upload_media_v3';
import DataSource from '@rendora/common/features/upload/oss-upload/data-source';
import { UPLOAD_STATUS } from '@rendora/common/types/enums';
import { UPLOAD_EVENTS, uploadEmitter, type UploadEventData } from '@rendora/common/utils/emitter';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import XOSSUpload from './oss-upload/service';
import type { IExtras, IServiceOptions, ISuccess } from './oss-upload/types';

// 上传文件项接口
export interface UploadFileItem {
  uid: string;
  resource: File;
  status: UPLOAD_STATUS;
  progress: number;
  error?: string;
  uploadSecretId?: string;
  media?: IMedia;
  uploader?: XOSSUpload; // 上传器实例
  uploadOptions: IServiceOptions; // 上传配置
  meta: Record<string, unknown>; // 元数据
  /** prepare 接口返回的 upload_full_name，用于上传模拟数据标题 */
  upload_full_name?: string;
}

// 上传文件项实体类，用于外部 instanceof 判断
export class EntityUploadFileItem implements UploadFileItem {
  uid: string;
  resource: File;
  status: UPLOAD_STATUS;
  progress: number;
  error?: string;
  uploadSecretId?: string;
  media?: IMedia;
  uploader?: XOSSUpload;
  uploadOptions: IServiceOptions;
  meta: Record<string, unknown>;
  upload_full_name?: string;

  constructor(data: UploadFileItem) {
    this.uid = data.uid;
    this.resource = data.resource;
    this.status = data.status;
    this.progress = data.progress;
    this.error = data.error;
    this.uploadSecretId = data.uploadSecretId;
    this.media = data.media;
    this.uploader = data.uploader;
    this.uploadOptions = data.uploadOptions;
    this.meta = data.meta;
    this.upload_full_name = data.upload_full_name;
  }
}

// 上传配置接口
export interface UploadConfig {
  maxConcurrent: number; // 最大并发上传数
}

// 上传回调接口
export interface UploadCallbacks {
  onProgress?: (file: EntityUploadFileItem, progress: number) => void;
  onSuccess?: (file: EntityUploadFileItem, media: IMedia) => void;
  onError?: (file: EntityUploadFileItem, error: string) => void;
  onComplete?: (file: EntityUploadFileItem) => void;
  onQueueChange?: (queue: EntityUploadFileItem[]) => void;
  onAllComplete?: (queue: EntityUploadFileItem[]) => void; // 所有任务完成回调
}

// 默认配置
const DEFAULT_CONFIG: UploadConfig = {
  maxConcurrent: 3,
};

export const useUploadStore = defineStore('upload', () => {
  // 状态管理
  const uploadQueue = ref<EntityUploadFileItem[]>([]);
  const uploadingFiles = ref<Set<string>>(new Set());
  const config = ref<UploadConfig>({ ...DEFAULT_CONFIG });
  const callbacks = ref<UploadCallbacks>({});

  // 计算属性
  const totalFiles = computed(() => uploadQueue.value.length);
  const pendingFiles = computed(() => filterFilesByStatus(UPLOAD_STATUS.NO_START));
  const uploadingFilesList = computed(() => filterFilesByStatus(UPLOAD_STATUS.UPLOADING));
  const completedFiles = computed(() =>
    filterFilesByStatus([UPLOAD_STATUS.FINISHED, UPLOAD_STATUS.ERROR]),
  );
  const successFiles = computed(() => filterFilesByStatus(UPLOAD_STATUS.FINISHED));
  const failedFiles = computed(() => filterFilesByStatus(UPLOAD_STATUS.ERROR));

  // 总体进度计算
  const overallProgress = computed(() => {
    if (totalFiles.value === 0) return 0;
    const totalProgress = uploadQueue.value.reduce((sum, file) => sum + file.progress, 0);
    return Math.round(totalProgress / totalFiles.value);
  });

  // 生成唯一ID
  const generateUid = (): string => {
    return `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // 根据UID查找文件项
  const findFileByUid = (uid: string): EntityUploadFileItem | undefined => {
    return uploadQueue.value.find(file => file.uid === uid) as EntityUploadFileItem | undefined;
  };

  // 根据UID查找文件项索引
  const findFileIndexByUid = (uid: string): number => {
    return uploadQueue.value.findIndex(file => file.uid === uid);
  };

  // 更新文件项属性（确保响应式触发）
  const updateFileItem = (uid: string, updates: Partial<UploadFileItem>, shouldNotify = true) => {
    const index = findFileIndexByUid(uid);
    if (index !== -1) {
      // 直接更新对象属性，Vue的响应式系统会自动检测
      Object.assign(uploadQueue.value[index], updates);

      // eslint-disable-next-line no-console
      console.log('- 更新文件项:', uploadQueue.value[index]);

      // 只在需要时通知队列变化，避免频繁触发
      if (shouldNotify) {
        notifyQueueChange();
      }
    }
  };

  // 统一的事件发射函数
  const emitEvent = <T extends keyof UploadEventData>(event: T, data: UploadEventData[T]) => {
    uploadEmitter.emit(event, data);
  };

  // 通知队列变化
  const notifyQueueChange = () => {
    callbacks.value.onQueueChange?.(uploadQueue.value as EntityUploadFileItem[]);
    emitEvent(UPLOAD_EVENTS.QUEUE_CHANGE, { queue: uploadQueue.value as EntityUploadFileItem[] });
  };

  // 检查所有任务是否完成
  const checkAllTasksComplete = () => {
    // 检查是否有待上传或正在上传的文件
    const hasActiveTasks = uploadQueue.value.some(
      file => file.status === UPLOAD_STATUS.NO_START || file.status === UPLOAD_STATUS.UPLOADING,
    );

    // 如果没有活跃任务且队列不为空，说明所有任务都已完成
    if (!hasActiveTasks && uploadQueue.value.length > 0) {
      callbacks.value.onAllComplete?.(uploadQueue.value as EntityUploadFileItem[]);
      // 发射所有任务完成事件
      emitEvent(UPLOAD_EVENTS.ALL_COMPLETE, { queue: uploadQueue.value as EntityUploadFileItem[] });
    }
  };

  // 根据状态过滤文件
  const filterFilesByStatus = (status: UPLOAD_STATUS | UPLOAD_STATUS[]) => {
    const statuses = Array.isArray(status) ? status : [status];
    return uploadQueue.value.filter(file => statuses.includes(file.status));
  };

  // 添加文件到上传队列（OSS）
  const addFiles = (
    files: File[],
    uploadOptions: IServiceOptions,
    meta: Record<string, unknown>,
  ) => {
    const newFiles: EntityUploadFileItem[] = files.map(
      file =>
        new EntityUploadFileItem({
          uid: generateUid(),
          resource: file,
          status: UPLOAD_STATUS.NO_START,
          progress: 0,
          uploader: undefined,
          uploadOptions, // 存储上传配置
          meta,
        }),
    );

    uploadQueue.value = [...uploadQueue.value, ...newFiles];

    // 立即触发队列变动事件，让UI立即响应
    notifyQueueChange();
  };

  // 开始上传队列中的所有待上传文件
  const startAllUploads = () => {
    processQueue();
  };

  // 处理上传队列
  const processQueue = async () => {
    const availableSlots = config.value.maxConcurrent - uploadingFiles.value.size;
    if (availableSlots <= 0) return;

    const filesToUpload = pendingFiles.value.slice(0, availableSlots);

    // 并发启动上传，不需要循环检查
    filesToUpload.forEach(fileItem => startUpload(fileItem as EntityUploadFileItem));
  };

  // 开始上传单个文件
  const startUpload = async (fileItem: EntityUploadFileItem) => {
    if (uploadingFiles.value.has(fileItem.uid)) {
      return;
    }

    uploadingFiles.value.add(fileItem.uid);

    updateFileItem(fileItem.uid, {
      status: UPLOAD_STATUS.UPLOADING,
      progress: 0,
    });

    // 立即触发队列变动事件，让UI立即响应
    notifyQueueChange();

    // 发射开始上传事件
    emitEvent(UPLOAD_EVENTS.START, { fileUid: fileItem.uid });

    try {
      // 统一走 OSS 上传
      const serviceOptions = {
        ...fileItem.uploadOptions, // 使用文件项中存储的配置
        ignore_query_status: true,
        onProgress: (progress: number, _extras: IExtras) => {
          updateFileItem(
            fileItem.uid,
            {
              status: UPLOAD_STATUS.UPLOADING,
              progress,
              media: _extras.media,
              ...(_extras.upload_full_name != null && {
                upload_full_name: _extras.upload_full_name,
              }),
            },
            false,
          );

          callbacks.value.onProgress?.(fileItem, progress);
          emitEvent(UPLOAD_EVENTS.PROGRESS, { fileUid: fileItem.uid, progress });
        },
        onSuccess: (media: ISuccess) => {
          updateFileItem(fileItem.uid, {
            status: UPLOAD_STATUS.FINISHED,
            progress: 100,
            media,
          });
          callbacks.value.onSuccess?.(fileItem, media);
          callbacks.value.onComplete?.(fileItem);
          emitEvent(UPLOAD_EVENTS.SUCCESS, { fileUid: fileItem.uid, media });
          emitEvent(UPLOAD_EVENTS.COMPLETE, { fileUid: fileItem.uid, media });
          setTimeout(checkAllTasksComplete, 0);
          setTimeout(clearCompleted, 0);
        },
        onFail: (error: string, _extras: IExtras) => {
          handleUploadError(fileItem, error);
        },
      };

      const uploader = new XOSSUpload({
        ...serviceOptions,
        dataSource: DataSource,
      });

      fileItem.uploader = uploader;

      // 开始上传
      await uploader.upload(fileItem.resource as File & { uid: string });
    } catch (error) {
      handleUploadError(fileItem, String(error));
    } finally {
      uploadingFiles.value.delete(fileItem.uid);
      processQueue();
    }
  };

  // 处理上传错误
  const handleUploadError = (fileItem: EntityUploadFileItem, error: string) => {
    updateFileItem(fileItem.uid, {
      error,
      status: UPLOAD_STATUS.ERROR,
    });
    callbacks.value.onError?.(fileItem, error);
    callbacks.value.onComplete?.(fileItem);

    // 统一发射事件
    emitEvent(UPLOAD_EVENTS.ERROR, { fileUid: fileItem.uid, error });
    emitEvent(UPLOAD_EVENTS.COMPLETE, { fileUid: fileItem.uid, media: fileItem.media! });

    // 检查是否所有任务都已完成
    setTimeout(checkAllTasksComplete, 0);
  };

  // 取消上传：中断上传操作并删除上传任务（从队列中移除）
  const cancelUpload = (uid: string) => {
    const index = uploadQueue.value.findIndex(file => file.uid === uid);
    if (index === -1) return;

    const fileItem = uploadQueue.value[index];

    // 中断 OSS 上传
    if (
      fileItem.uploader &&
      typeof fileItem.uploader === 'object' &&
      'cancel' in fileItem.uploader
    ) {
      fileItem.uploader.cancel?.({ deleteData: true });
    }

    uploadQueue.value.splice(index, 1);
    uploadingFiles.value.delete(uid);

    emitEvent(UPLOAD_EVENTS.CANCEL, { fileUid: uid });
    emitEvent(UPLOAD_EVENTS.REMOVE, { fileUid: uid });
    notifyQueueChange();
  };

  // 移除文件
  const removeFile = (uid: string) => {
    const index = uploadQueue.value.findIndex(file => file.uid === uid);
    if (index === -1) return;

    const fileItem = uploadQueue.value[index];

    // 如果正在上传，调用 cancelUpload 中断并删除
    if (fileItem.status === UPLOAD_STATUS.UPLOADING) {
      cancelUpload(uid);
      return;
    }

    uploadQueue.value.splice(index, 1);
    uploadingFiles.value.delete(uid);

    emitEvent(UPLOAD_EVENTS.REMOVE, { fileUid: uid });
    notifyQueueChange();
  };

  // 清空队列
  const clearQueue = () => {
    // 取消所有正在上传的文件
    uploadingFilesList.value.forEach(file => {
      cancelUpload(file.uid);
    });

    uploadQueue.value = [];
    uploadingFiles.value.clear();

    // 发射清空队列事件
    emitEvent(UPLOAD_EVENTS.CLEAR_QUEUE, undefined);

    notifyQueueChange();
  };

  // 清空已完成文件
  const clearCompleted = () => {
    // 从后往前删除，避免索引变化问题
    for (let i = uploadQueue.value.length - 1; i >= 0; i--) {
      const file = uploadQueue.value[i];
      if (file.status === UPLOAD_STATUS.FINISHED || file.status === UPLOAD_STATUS.ERROR) {
        removeFile(file.uid);
      }
    }

    // 发射清空已完成文件事件
    emitEvent(UPLOAD_EVENTS.CLEAR_COMPLETED, undefined);
  };

  // 更新配置
  const updateConfig = (newConfig: Partial<UploadConfig>) => {
    config.value = { ...config.value, ...newConfig };
  };

  // 设置回调函数
  const setCallbacks = (newCallbacks: UploadCallbacks) => {
    callbacks.value = { ...callbacks.value, ...newCallbacks };
  };

  // 获取文件信息
  const getFile = (uid: string): EntityUploadFileItem | undefined => {
    return findFileByUid(uid);
  };

  // 获取上传统计信息
  const getStats = () => {
    return {
      total: totalFiles.value,
      pending: pendingFiles.value.length,
      uploading: uploadingFilesList.value.length,
      success: successFiles.value.length,
      failed: failedFiles.value.length,
      progress: overallProgress.value,
    };
  };

  return {
    // 状态
    uploadQueue,
    uploadingFiles,
    config,
    callbacks,

    // 计算属性
    totalFiles,
    pendingFiles,
    uploadingFilesList,
    completedFiles,
    successFiles,
    failedFiles,
    overallProgress,

    // 方法
    addFiles,
    startAllUploads,
    cancelUpload,
    removeFile,
    clearQueue,
    clearCompleted,
    updateConfig,
    setCallbacks,
    getFile,
    getStats,
    checkAllTasksComplete, // 手动检查所有任务是否完成

    // 事件发射器
    emitter: uploadEmitter, // 导出事件发射器供外部使用
  };
});
